import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Sparkles, Mail, Lock, User, Building2, Phone, Eye, EyeOff,
  Check, X, ArrowRight, Shield, Zap, ArrowLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Basic info, Step 2: Social Media Auth
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    company_name: "",
    phone: "",
    terms_accepted: false,
    privacy_policy_accepted: false,
    marketing_consent: false,
    // Social Media Authorization
    social_media_auth_type: "none", // none, manage_existing, create_and_manage, hybrid
    social_media_auth_accepted: false,
    social_media_platforms: [],
    existing_platform_handles: {},
    legal_signature_name: "",
    title_position: ""
  });
  
  const [errors, setErrors] = useState({});

  // Available platforms
  const platforms = [
    { id: "twitter", name: "X (Twitter)", icon: "𝕏" },
    { id: "linkedin", name: "LinkedIn", icon: "in" },
    { id: "instagram", name: "Instagram", icon: "📷" },
    { id: "facebook", name: "Facebook", icon: "f" },
    { id: "tiktok", name: "TikTok", icon: "♪" },
    { id: "youtube", name: "YouTube", icon: "▶" }
  ];

  // Toggle platform selection
  const togglePlatform = (platformId) => {
    setFormData(prev => {
      const platforms = prev.social_media_platforms.includes(platformId)
        ? prev.social_media_platforms.filter(p => p !== platformId)
        : [...prev.social_media_platforms, platformId];
      return { ...prev, social_media_platforms: platforms };
    });
  };

  // Update existing handle
  const updateExistingHandle = (platformId, handle) => {
    setFormData(prev => ({
      ...prev,
      existing_platform_handles: {
        ...prev.existing_platform_handles,
        [platformId]: handle
      }
    }));
  };

  // Password strength checker
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const strengthLabels = ["", "Weak", "Fair", "Good", "Strong", "Excellent"];
  const strengthColors = ["", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500", "bg-emerald-500"];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    
    if (!formData.full_name) {
      newErrors.full_name = "Full name is required";
    }
    
    // CRITICAL: Mandatory consent validation
    if (!formData.terms_accepted) {
      newErrors.terms_accepted = "You must accept the Terms of Service to create an account";
    }
    
    if (!formData.privacy_policy_accepted) {
      newErrors.privacy_policy_accepted = "You must accept the Privacy Policy to create an account";
    }
    
    // Social Media Authorization validation
    if (formData.social_media_auth_type !== "none" && formData.social_media_platforms.length > 0) {
      if (!formData.social_media_auth_accepted) {
        newErrors.social_media_auth = "You must accept the Social Media Authorization";
      }
      if (!formData.legal_signature_name) {
        newErrors.legal_signature_name = "Legal signature is required for Social Media Authorization";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors below");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, formData);
      
      // Store token and user data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success("Account created successfully! Welcome aboard!");
      
      // Redirect to dashboard
      navigate('/dashboard');
      
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#030303]">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="YASNO" className="w-12 h-12 object-contain" />
            <div>
              <span className="text-2xl font-bold tracking-tight">
                YAS<span className="text-primary">NO</span>
              </span>
              <p className="text-xs text-muted-foreground">AI Marketing Platform</p>
            </div>
          </Link>

          <h1 className="text-2xl font-bold mb-2">Create your account</h1>
          <p className="text-muted-foreground mb-8">
            Start automating your marketing with AI
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="John Doe"
                  className={`pl-10 h-12 bg-white/5 border-white/10 ${errors.full_name ? 'border-red-500' : ''}`}
                  value={formData.full_name}
                  onChange={handleInputChange}
                  data-testid="register-name"
                />
              </div>
              {errors.full_name && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <X className="w-4 h-4" /> {errors.full_name}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@company.com"
                  className={`pl-10 h-12 bg-white/5 border-white/10 ${errors.email ? 'border-red-500' : ''}`}
                  value={formData.email}
                  onChange={handleInputChange}
                  data-testid="register-email"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <X className="w-4 h-4" /> {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 8 characters"
                  className={`pl-10 pr-10 h-12 bg-white/5 border-white/10 ${errors.password ? 'border-red-500' : ''}`}
                  value={formData.password}
                  onChange={handleInputChange}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Password strength indicator */}
              {formData.password && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          passwordStrength >= level ? strengthColors[passwordStrength] : 'bg-white/10'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Password strength: <span className={passwordStrength >= 3 ? 'text-green-400' : 'text-orange-400'}>
                      {strengthLabels[passwordStrength]}
                    </span>
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <X className="w-4 h-4" /> {errors.password}
                </p>
              )}
            </div>

            {/* Company Name (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name (Optional)</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="company_name"
                  name="company_name"
                  type="text"
                  placeholder="Your company"
                  className="pl-10 h-12 bg-white/5 border-white/10"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  data-testid="register-company"
                />
              </div>
            </div>

            {/* Consent Section - CRITICAL */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-primary" />
                <span>By creating an account, you agree to:</span>
              </div>
              
              {/* Terms of Service - MANDATORY */}
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms_accepted"
                    checked={formData.terms_accepted}
                    onCheckedChange={(checked) => handleCheckboxChange('terms_accepted', checked)}
                    className={errors.terms_accepted ? 'border-red-500' : ''}
                    data-testid="terms-checkbox"
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor="terms_accepted"
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      I accept the <a href="/terms" className="text-primary hover:underline" target="_blank">Terms of Service</a> *
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Required to use our AI-powered marketing services
                    </p>
                  </div>
                </div>
                {errors.terms_accepted && (
                  <p className="text-sm text-red-500 flex items-center gap-1 ml-7">
                    <X className="w-4 h-4" /> {errors.terms_accepted}
                  </p>
                )}
              </div>

              {/* Privacy Policy - MANDATORY */}
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy_policy_accepted"
                    checked={formData.privacy_policy_accepted}
                    onCheckedChange={(checked) => handleCheckboxChange('privacy_policy_accepted', checked)}
                    className={errors.privacy_policy_accepted ? 'border-red-500' : ''}
                    data-testid="privacy-checkbox"
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor="privacy_policy_accepted"
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      I accept the <a href="/privacy" className="text-primary hover:underline" target="_blank">Privacy Policy</a> *
                    </label>
                    <p className="text-xs text-muted-foreground">
                      How we handle your data and protect your privacy
                    </p>
                  </div>
                </div>
                {errors.privacy_policy_accepted && (
                  <p className="text-sm text-red-500 flex items-center gap-1 ml-7">
                    <X className="w-4 h-4" /> {errors.privacy_policy_accepted}
                  </p>
                )}
              </div>

              {/* Marketing Consent - OPTIONAL */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="marketing_consent"
                  checked={formData.marketing_consent}
                  onCheckedChange={(checked) => handleCheckboxChange('marketing_consent', checked)}
                  data-testid="marketing-checkbox"
                />
                <div className="space-y-1">
                  <label
                    htmlFor="marketing_consent"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    I'd like to receive marketing updates (Optional)
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Tips, product news, and special offers via email
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media Authorization Section */}
            <div className="space-y-4 p-4 rounded-xl bg-gradient-to-br from-primary/10 to-purple-500/10 border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Social Media Authorization</h3>
              </div>
              
              <p className="text-sm text-muted-foreground">
                To enable AI-powered social media marketing, please authorize YASNO to manage your accounts.
              </p>

              {/* Authorization Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm">Do you have existing social media accounts for your business?</Label>
                <div className="grid gap-2">
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.social_media_auth_type === 'manage_existing' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}>
                    <input
                      type="radio"
                      name="social_media_auth_type"
                      value="manage_existing"
                      checked={formData.social_media_auth_type === 'manage_existing'}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_media_auth_type: e.target.value }))}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.social_media_auth_type === 'manage_existing' ? 'border-primary' : 'border-white/30'
                    }`}>
                      {formData.social_media_auth_type === 'manage_existing' && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-sm">Yes, I have accounts</span>
                      <p className="text-xs text-muted-foreground">I'll connect my existing accounts via OAuth</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.social_media_auth_type === 'no_accounts' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}>
                    <input
                      type="radio"
                      name="social_media_auth_type"
                      value="no_accounts"
                      checked={formData.social_media_auth_type === 'no_accounts'}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_media_auth_type: e.target.value }))}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.social_media_auth_type === 'no_accounts' ? 'border-primary' : 'border-white/30'
                    }`}>
                      {formData.social_media_auth_type === 'no_accounts' && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-sm">I don't have social media accounts yet</span>
                      <p className="text-xs text-muted-foreground">You'll need to create accounts on your preferred platforms first, then connect them here</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.social_media_auth_type === 'hybrid' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}>
                    <input
                      type="radio"
                      name="social_media_auth_type"
                      value="hybrid"
                      checked={formData.social_media_auth_type === 'hybrid'}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_media_auth_type: e.target.value }))}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.social_media_auth_type === 'hybrid' ? 'border-primary' : 'border-white/30'
                    }`}>
                      {formData.social_media_auth_type === 'hybrid' && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-sm">Mix - I have some, need others created</span>
                      <p className="text-xs text-muted-foreground">I'll specify which platforms I have</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.social_media_auth_type === 'none' 
                      ? 'border-white/20 bg-white/5' 
                      : 'border-white/10 hover:border-white/20'
                  }`}>
                    <input
                      type="radio"
                      name="social_media_auth_type"
                      value="none"
                      checked={formData.social_media_auth_type === 'none'}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_media_auth_type: e.target.value }))}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.social_media_auth_type === 'none' ? 'border-white/50' : 'border-white/30'
                    }`}>
                      {formData.social_media_auth_type === 'none' && (
                        <div className="w-2 h-2 rounded-full bg-white/50" />
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-sm text-muted-foreground">Skip for now</span>
                      <p className="text-xs text-muted-foreground">I'll set this up later in dashboard</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Message for users without accounts */}
              {formData.social_media_auth_type === 'no_accounts' && (
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <p className="text-sm text-yellow-400 font-medium mb-2">📱 Create Your Social Media Accounts First</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    To use YASNO's automated marketing, you'll need accounts on your preferred platforms. Create them using the links below, then come back to connect them:
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <a href="https://www.tiktok.com/signup" target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors">TikTok →</a>
                    <a href="https://www.instagram.com/accounts/emailsignup/" target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors">Instagram →</a>
                    <a href="https://www.facebook.com/reg" target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors">Facebook →</a>
                    <a href="https://twitter.com/i/flow/signup" target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors">Twitter/X →</a>
                    <a href="https://www.youtube.com/create_channel" target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors">YouTube →</a>
                    <a href="https://www.linkedin.com/signup" target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors">LinkedIn →</a>
                  </div>
                </div>
              )}

              {/* Platform Selection - Show if auth type is not 'none' and not 'no_accounts' */}
              {formData.social_media_auth_type !== 'none' && formData.social_media_auth_type !== 'no_accounts' && (
                <div className="space-y-3 pt-2">
                  <Label className="text-sm">Select platforms to authorize:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {platforms.map((platform) => (
                      <button
                        key={platform.id}
                        type="button"
                        onClick={() => togglePlatform(platform.id)}
                        className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all ${
                          formData.social_media_platforms.includes(platform.id)
                            ? 'border-primary bg-primary/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <span className="text-lg w-6 text-center">{platform.icon}</span>
                        <span className="text-sm">{platform.name}</span>
                        {formData.social_media_platforms.includes(platform.id) && (
                          <Check className="w-4 h-4 text-primary ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing Handles - Show for 'manage_existing' or 'hybrid' */}
              {(formData.social_media_auth_type === 'manage_existing' || formData.social_media_auth_type === 'hybrid') && 
               formData.social_media_platforms.length > 0 && (
                <div className="space-y-3 pt-2">
                  <Label className="text-sm">Your existing account handles (optional):</Label>
                  <div className="space-y-2">
                    {formData.social_media_platforms.map((platformId) => {
                      const platform = platforms.find(p => p.id === platformId);
                      return (
                        <div key={platformId} className="flex items-center gap-2">
                          <span className="text-sm w-20">{platform?.name}:</span>
                          <Input
                            placeholder={`@your${platformId}handle`}
                            value={formData.existing_platform_handles[platformId] || ''}
                            onChange={(e) => updateExistingHandle(platformId, e.target.value)}
                            className="flex-1 h-9 bg-white/5 border-white/10 text-sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Legal Authorization - Show if platforms selected */}
              {formData.social_media_auth_type !== 'none' && formData.social_media_auth_type !== 'no_accounts' && formData.social_media_platforms.length > 0 && (
                <div className="space-y-3 pt-3 border-t border-white/10">
                  <div className="p-3 rounded-lg bg-black/30 text-xs text-muted-foreground max-h-32 overflow-y-auto">
                    <strong className="text-foreground">SOCIAL MEDIA MANAGEMENT AUTHORIZATION</strong>
                    <p className="mt-2">
                      By checking below and signing, I authorize YASNO Marketing LLC to:
                        </p>
                        <ul className="list-disc ml-4 mt-1 space-y-1">
                          <li>ACCESS my existing social media accounts</li>
                          <li>POST content on my behalf</li>
                          <li>MANAGE my social media presence</li>
                          <li>ACCESS analytics and performance data</li>
                        </ul>
                    <p className="mt-2">This authorization can be revoked at any time through the dashboard.</p>
                  </div>

                  {/* Legal Signature */}
                  <div className="space-y-2">
                    <Label htmlFor="legal_signature_name" className="text-sm">Digital Signature (Full Legal Name) *</Label>
                    <Input
                      id="legal_signature_name"
                      placeholder="Type your full legal name to sign"
                      value={formData.legal_signature_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, legal_signature_name: e.target.value }))}
                      className={`h-10 bg-white/5 border-white/10 font-serif italic ${errors.legal_signature_name ? 'border-red-500' : ''}`}
                    />
                    {errors.legal_signature_name && (
                      <p className="text-xs text-red-500">{errors.legal_signature_name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="title_position" className="text-sm">Your Title/Position (Optional)</Label>
                    <Input
                      id="title_position"
                      placeholder="e.g., CEO, Marketing Manager"
                      value={formData.title_position}
                      onChange={(e) => setFormData(prev => ({ ...prev, title_position: e.target.value }))}
                      className="h-10 bg-white/5 border-white/10"
                    />
                  </div>

                  {/* Authorization Checkbox */}
                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox
                      id="social_media_auth_accepted"
                      checked={formData.social_media_auth_accepted}
                      onCheckedChange={(checked) => handleCheckboxChange('social_media_auth_accepted', checked)}
                      className={errors.social_media_auth ? 'border-red-500' : ''}
                    />
                    <div className="space-y-1">
                      <label
                        htmlFor="social_media_auth_accepted"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        I authorize YASNO to manage social media accounts on my behalf *
                      </label>
                    </div>
                  </div>
                  {errors.social_media_auth && (
                    <p className="text-xs text-red-500">{errors.social_media_auth}</p>
                  )}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base"
              disabled={isLoading}
              data-testid="register-submit"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Create Account
                  <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </Button>

            {/* Sign In Link */}
            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </form>
        </motion.div>
      </div>

      {/* Right Panel - Hero Image/Info (Hidden on mobile) */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-primary/20 to-blue-600/20 items-center justify-center p-12">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1726066012749-f81bf4422d4e?w=1200')] bg-cover bg-center opacity-10" />
        <div className="relative text-center max-w-lg">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-cyan-500/30">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Your AI Marketing Team Awaits</h2>
          <p className="text-muted-foreground mb-8">
            Join thousands of businesses using voice-activated AI to automate their entire marketing workflow.
          </p>
          <div className="flex justify-center gap-8">
            {[
              { value: "85%", label: "Time Saved" },
              { value: "10x", label: "Content Output" },
              { value: "12", label: "Languages" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl font-bold text-primary">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
